import React from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiUsers, FiTarget, FiTrendingUp } from 'react-icons/fi';
import './About.css';

const About = () => {
  const stats = [
    { icon: FiAward, number: '10+', label: 'Years Experience' },
    { icon: FiUsers, number: '50K+', label: 'Happy Customers' },
    { icon: FiTarget, number: '100+', label: 'Countries Served' },
    { icon: FiTrendingUp, number: '99%', label: 'Success Rate' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <div className="about page-transition">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="about-hero-title">About Couriex</h1>
            <p className="about-hero-subtitle">
              Your trusted partner in logistics and courier services
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="about-stats">
        <div className="container">
          <motion.div
            className="stats-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="stat-icon">
                  <stat.icon size={32} />
                </div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="about-content">
        <div className="container">
          <div className="about-content-grid">
            <motion.div
              className="about-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="about-section-title">Our Story</h2>
              <p className="about-text-content">
                Founded with a vision to revolutionize the courier and logistics
                industry, Couriex has been at the forefront of delivering excellence
                for over a decade. We started as a small local courier service and
                have grown into a trusted global logistics partner.
              </p>
              <p className="about-text-content">
                Our commitment to quality, reliability, and customer satisfaction has
                made us a preferred choice for businesses and individuals worldwide.
                We understand that every package is important, and we treat it with
                the care and attention it deserves.
              </p>
            </motion.div>

            <motion.div
              className="about-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="about-image-placeholder">
                <FiUsers size={150} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values">
        <div className="container">
          <motion.div
            className="values-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Our Values</h2>
            <p className="section-description">
              The principles that guide everything we do
            </p>
          </motion.div>

          <motion.div
            className="values-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              {
                title: 'Reliability',
                description:
                  'We deliver on our promises, every single time. Your trust is our most valuable asset.',
              },
              {
                title: 'Innovation',
                description:
                  'We continuously improve our services and embrace new technologies to serve you better.',
              },
              {
                title: 'Excellence',
                description:
                  'We strive for perfection in every aspect of our service, from handling to delivery.',
              },
              {
                title: 'Customer Focus',
                description:
                  'Your satisfaction is our priority. We listen, adapt, and go the extra mile for you.',
              },
            ].map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;

